import { useStandardTree, useLEStandardReport } from '../../hooks';
import { useLessonTree, useLELessonReport } from '../../hooks';
import { useSkillTree, useLESkillReport } from '../../hooks';
import { ReportTreeNode } from '../../types';

export type ReportType = 'skill' | 'standard' | 'lesson';
type TreeData = ReportTreeNode | null | undefined;

export interface UseReportHooksResult {
  reportTreeStructure: ReportTreeNode | null;
  reportData: any;
  isLoading: boolean;
  treeNode: ReportTreeNode | undefined;
}

/**
 * Extracts the display root from report tree structure.
 * API returns a single root ReportTreeNode; when it has children, we use the first child as the display root.
 */
const getRootNode = (reportTreeStructure: TreeData): ReportTreeNode | undefined => {
  if (!reportTreeStructure) {
    return undefined;
  }
  if (reportTreeStructure.subNodes?.length > 0) {
    return reportTreeStructure.subNodes[0];
  }
  return reportTreeStructure;
};

export interface UseReportHooksOptions {
  enabled?: boolean;
}

/**
 * Hook selector that returns the appropriate tree and report hooks based on reportType.
 * Fetches all report data and returns the relevant tree, report data, and loading state for the specified report type.
 */
export const useReportHooks = (
  reportType: ReportType,
  curriculumsubject: number,
  examId: number,
  sectionId?: string | number,
  options?: UseReportHooksOptions,
): UseReportHooksResult => {
  const enabled = options?.enabled ?? true;
  const isStandardEnabled = enabled && reportType === 'standard';
  const isLessonEnabled = enabled && reportType === 'lesson';
  const isSkillEnabled = enabled && reportType === 'skill';

  const { data: standardTreeData, isLoading: standardTreeLoading } = useStandardTree(
    curriculumsubject,
    sectionId,
    { enabled: isStandardEnabled },
  );
  
  const standardScoreData = useLEStandardReport(examId, [curriculumsubject], sectionId, {
    enabled: isStandardEnabled,
  });

  const { data: lessonTreeData, isLoading: lessonTreeLoading } = useLessonTree(
    curriculumsubject,
    sectionId,
    { enabled: isLessonEnabled },
  );
  const lessonScoreData = useLELessonReport(examId, [curriculumsubject], sectionId, {
    enabled: isLessonEnabled,
  });

  const { data: skillTreeData, isLoading: skillTreeLoading } = useSkillTree(
    curriculumsubject,
    sectionId,
    { enabled: isSkillEnabled },
  );
  const skillScoreData = useLESkillReport(examId, [curriculumsubject], sectionId, {
    enabled: isSkillEnabled,
  });

  switch (reportType) {
    case 'standard':
      return {
        reportTreeStructure: standardTreeData || null,
        reportData: standardScoreData,
        isLoading: standardTreeLoading,
        treeNode: getRootNode(standardTreeData),
      };
    case 'lesson':
      return {
        reportTreeStructure: lessonTreeData || null,
        reportData: lessonScoreData,
        isLoading: lessonTreeLoading,
        treeNode: getRootNode(lessonTreeData),
      };
    case 'skill':
      return {
        reportTreeStructure: skillTreeData || null,
        reportData: skillScoreData,
        isLoading: skillTreeLoading,
        treeNode: getRootNode(skillTreeData),
      };
    default:
      return {
        reportTreeStructure: standardTreeData || null,
        reportData: standardScoreData,
        isLoading: standardTreeLoading,
        treeNode: getRootNode(standardTreeData),
      };
  }
};
